export default {
  methods: {
    /**
     * Predict font color based on background color
     * @param {String} bgColor Background color in RGB to evaluate
     * @param {String} lightColor fontColor suggested if bgColor is deemed dark shade, default to be black
     * @param {String} darkColor fontColor suggested if bgColor is deemed light shade, default to be iview gray
     * @return {String} fontColor to be light or dark
     * @see https://gist.github.com/dcondrey/183971f17808e9277572#file-contrast-color-js
     * @see https://stackoverflow.com/questions/3942878/how-to-decide-font-color-in-white-or-black-depending-on-background-color
     */
    pickTextColorBasedOnBgColor (bgColor = '#FFFFFF', lightColor = '#FFFFFF', darkColor = '#515A6E') {
      const [r, g, b] = bgColor.substring(4, bgColor.length - 1)
        .replace(/ /g, '')
        .split(',')

      // calculate contrast of color (standard grayscale algorithmic formula)
      const contrast = (Math.round(r * 299) + Math.round(g * 587) + Math.round(b * 114)) / 1000
      return (contrast >= 128) ? darkColor : lightColor
    },

    // TODO - merge color and background color calculation functions
    /**
     * Calculate color
     * @param {Object} columnObject contains three params: comparedColumn, valueRange and colorRange
     * @param {Number} changeNumber
     * @return {String} rgb color
     */
    calculateColor (columnObject, changeNumber) {
      const {
        valueRange = [],
        colorRange = []
      } = columnObject

      if (!valueRange.length || !colorRange.length) {
        return ''
      }

      if (changeNumber < valueRange[0]) {
        return `rgb(${this.convertHexToRgb(colorRange[0])})`
      } else if (changeNumber > valueRange[1]) {
        return `rgb(${this.convertHexToRgb(colorRange[1])})`
      }

      const rgbColorRange = colorRange.map(range => this.convertHexToRgb(range))

      // Color calculation revised according to https://stackoverflow.com/a/24253254
      const w = changeNumber * 2 - 1
      const w1 = (w + 1) / 2
      const w2 = 1 - w1
      return `rgb(${parseInt(rgbColorRange[0][0] * w1 + rgbColorRange[1][0] * w2)},
        ${parseInt(rgbColorRange[0][1] * w1 + rgbColorRange[1][1] * w2)},
        ${parseInt(rgbColorRange[0][2] * w1 + rgbColorRange[1][2] * w2)}`
    },

    /**
     * Calculate background color
     * @param {Object} columnObject contains three params: comparedColumn, valueRange and colorRange
     * @param {Number} changeNumber
     * @return {String} rgb color
     */
    calculateBgColor (columnObject, changeNumber) {
      const {
        valueRange = [],
        colorRange = []
      } = columnObject

      if (!valueRange.length || !colorRange.length) {
        return ''
      }

      if (changeNumber < valueRange[0]) {
        return `rgb(${this.convertHexToRgb(colorRange[0])})`
      } else if (changeNumber > valueRange[1]) {
        return `rgb(${this.convertHexToRgb(colorRange[1])})`
      }

      const rgbColorRange = colorRange.map(range => this.convertHexToRgb(range))
      const middle = (valueRange[0] + valueRange[1]) / 2
      if (changeNumber >= middle) {
        const ratio = (changeNumber - middle) / (valueRange[1] - middle)
        return this.convertRgbaToRgb(...rgbColorRange[1], ratio)
      } else {
        const ratio = (middle - changeNumber) / (middle - valueRange[0])
        return this.convertRgbaToRgb(...rgbColorRange[0], ratio)
      }
    },

    /**
     * Convert rgba color array to rgb based on white RGB background
     * @param {Number} r red
     * @param {Number} g green
     * @param {Number} b blue
     * @param {Number} alpha
     * @return {String} rgb string
     */
    convertRgbaToRgb (r, g, b, alpha) {
      return `rgb(${(1 - alpha) * 255 + alpha * r}, ` +
        `${(1 - alpha) * 255 + alpha * g}, ` +
        `${(1 - alpha) * 255 + alpha * b})`
    },

    /**
     * Util function to convert Hex to RGB
     * @param {String} hex hex color without #
     * @return {Array} rgb presentation of color
     */
    convertHexToRgb (hex) {
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return [r, g, b]
    },

    /**
     * Format number based on type
     * @param {String/Number} value
     * @param {String} type
     * @param {Number} precision
     * @return {String} number in string type
     */
    formatNumber (value, type, precision = 2) {
      const parsed = this.validateNumber(value)
      switch (type) {
        case 'number':
          return this.toFixedDecimal(parsed, 0)
        case 'decimal':
          return this.toFixedDecimal(parsed, precision)
        case 'percentage':
          return this.toPercentage(parsed, precision)
        case 'currency':
          return this.toCurrencyDecimal(parsed, 0)
        case 'currencyDecimal':
          return this.toCurrencyDecimal(parsed, precision)
        default:
          return value
      }
    },

    /**
     * Util function to convert number to a number with two decimal places
     * @param {Number} value
     * @return {String} a number with two decimal places
     */
    toFixedDecimal (value, precision = 2) {
      return value ? Number(value).toLocaleString('en-US', {
        maximumFractionDigits: precision,
        minimumFractionDigits: precision
      }) : '--'
    },

    /**
     * Util function to convert number to string percentage with two decimal places
     * @param {Number} value
     * @param {Number} precision decimal precision to be converted
     * @return {String} percentage with two decimal places
     */
    toPercentage (value, precision = 2) {
      return value ? Number(value).toLocaleString('en-US', {
        style: 'percent',
        maximumFractionDigits: precision,
        minimumFractionDigits: precision
      }) : '--'
    },

    /**
     * Util function to convert number to a number with two decimal places and dollar sign
     * @param {Number} value
     * @param {Number} precision decimal precision to be converted
     * @return {String} a number with two decimal places
     */
    toCurrencyDecimal (value, precision = 2) {
      return value ? Number(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: precision,
        minimumFractionDigits: precision
      }) : '--'
    },

    /**
     * Util function to validate input string is a number
     * @param {String} value
     * @return {Number}
     */
    validateNumber (value) {
      const parsed = Number.parseFloat(value)
      return Number.isNaN(parsed) ? 0 : parsed
    }
  }
}
